import { initializeApp } from "firebase/app";
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  disableNetwork,
  connectFirestoreEmulator,
} from "firebase/firestore";
import { getAuth, GoogleAuthProvider, connectAuthEmulator } from "firebase/auth";
import { getDatabase, connectDatabaseEmulator } from "firebase/database";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { capAuthBootRequests } from "./utils/authBootTimeout";
import { EMULATOR_HOST, EMULATOR_PORTS, EMULATOR_PROJECT_ID, USE_EMULATORS } from "./utils/emulators";

// Local development against the Firebase emulators (`npm run dev:local`, see
// CLAUDE.md). A `demo-` project needs no credentials and can't reach a real
// backend, so none of the VITE_FIREBASE_* values are needed. Vite inlines
// USE_EMULATORS as `false` in every other build, so this and the connect calls
// below are dropped from production bundles.
const emulatorConfig = {
  apiKey: "demo-api-key",
  authDomain: `${EMULATOR_PROJECT_ID}.firebaseapp.com`,
  // The namespace the Functions emulator gives a demo project's Admin SDK.
  databaseURL: `https://${EMULATOR_PROJECT_ID}.firebaseio.com`,
  projectId: EMULATOR_PROJECT_ID,
  storageBucket: `${EMULATOR_PROJECT_ID}.appspot.com`,
  appId: "demo-app-id",
};

const firebaseConfig = USE_EMULATORS ? emulatorConfig : {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  // authDomain must NOT be the same origin as the installed PWA. We use
  // signInWithPopup, which calls window.open() on the auth handler URL.
  // If that URL is on the production hosting origin (where the user has
  // the PWA installed), Chrome's PWA link capturing hijacks the new
  // window into the installed standalone app, which then lands on a
  // 404 because /__/auth/handler isn't a SPA route. Forcing the default
  // <project>.firebaseapp.com auth domain keeps the popup on a separate
  // origin, so capture doesn't trigger and Firebase Hosting can serve
  // the real auth handler.
  authDomain: `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.firebaseapp.com`,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const app = initializeApp(firebaseConfig);

// Modern persistence API — replaces deprecated enableIndexedDbPersistence
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
});
// Must come before anything starts the instance, disableNetwork included.
if (USE_EMULATORS) connectFirestoreEmulator(db, EMULATOR_HOST, EMULATOR_PORTS.firestore);

// Boot cache-first: start with Firestore's network disabled so the very first
// listeners resolve from the local cache immediately, instead of hanging on the
// ~60s WebChannel long-poll timeout when connectivity is flaky (captive portals,
// cruise/satellite wifi that reports "online" but can't reach the server).
// `useFirestoreNetworkBridge` re-enables the network once a reachability probe
// confirms we can actually reach the network. Fire-and-forget; failures are
// harmless (the bridge will reconcile).
void disableNetwork(db).catch(() => {});

export const auth = getAuth(app);
if (USE_EMULATORS) {
  connectAuthEmulator(auth, `http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}`, { disableWarnings: true });
}
// Boot from the saved sign-in instead of waiting up to a minute on a token
// refresh over bad wifi; see capAuthBootRequests.
capAuthBootRequests(auth);
export const rtdb = getDatabase(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, "us-central1");
if (USE_EMULATORS) {
  connectDatabaseEmulator(rtdb, EMULATOR_HOST, EMULATOR_PORTS.database);
  connectStorageEmulator(storage, EMULATOR_HOST, EMULATOR_PORTS.storage);
  connectFunctionsEmulator(functions, EMULATOR_HOST, EMULATOR_PORTS.functions);
}
export const googleProvider = new GoogleAuthProvider();
